/**
 * Coalesce multiple calls to a function into a single call by reducing the arguments
 * provided over a time window.
 *
 * Example:
 *
 * ```ts
 * const fn = coalesce((state) => {
 *   console.log(state);
 * }, (msgs, ...args) => {
 *   return [...msgs, ...args];
 * }, { initial: () => [], afterStableMs: 1000 });
 *
 * fn('hello')
 * await sleep(100)
 * fn('world', 1, 2, 3)
 * fn('!')
 * // -> (after 1000ms) ['hello', 'world', 1, 2, 3]
 * ```
 *
 * @param fn
 * @param reduce
 * @param opts
 */
export function coalesce<A extends any[], S>(
  fn: (state: S) => void,
  reduce: (state: S, ...args: A) => S,
  opts: { initial(): S; afterStableMs: number }
): (...args: A) => void {
  let timeout: NodeJS.Timeout | null = null;
  let state!: S;
  state = opts.initial();
  return (...args: A) => {
    state = reduce(state, ...args);
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(flush, opts.afterStableMs);
  };

  function flush() {
    fn(state);
    state = opts.initial();
  }
}
